import { AgencyAlertKind, IAgencyClient, IAgencyDigest, IAgencyFinding, readCondition } from './agencyDigest';
import { classifyFinding } from './agencyFindingKind';
import { CONDITION_TOLERANCE, MIN_CLIENTS_FOR_A_RATE, MIN_RECENT_FINDINGS_TO_FLAG, RECENT_ROLLING_WEEKS } from './agencyThresholds';
import { CareDomain } from '../../insights/IInsight';
import { DOMAIN_LABEL, domainsOf, IWeekSpan, rollingWeeksTo } from './agencyDomains';
import { describeUnreadable } from './agencyNarration';
import { IOutcomeDefinition, OUTCOME_DEFINITIONS, OutcomeMetricId } from './agencyOutcomeDefinitions';

/**
 * What the caseload can show for itself: who came back from a decline, who has settled below their
 * own usual, and who had no incident recorded.
 *
 * These are outcome-shaped rather than activity-shaped, which is the whole point of them. Counting
 * observations proves the tool is being used; counting clients who came back to their own usual is
 * the thing an operator is actually buying. Both live on the same tab, and the difference between
 * them is worth keeping visible.
 *
 * What each figure counts is written once, in agencyOutcomeDefinitions.ts. This file computes it, and
 * a change here that changes what a figure means changes its definition in the same commit.
 *
 * The honest limit, unchanged from everywhere else on this tab: none of this establishes that
 * Innerhive CAUSED a recovery. Nobody has run the counterfactual. What the record supports is that
 * a client was above their own usual, then was not, and that these things were written down in
 * between. The copy says "what was recorded around the change", never "what fixed it".
 */

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** How many recovery notes a drill-down lists per client before it stops. */
const MAX_DRIVERS_PER_CLIENT = 2;

/** One row of a metric's drill-down: who, and the one line saying why they are on this list. */
export interface IOutcomeClient {
    careMapId: string;
    clientName: string;
    detail: string;
    /** What was written down around the change. Never presented as the cause of it. */
    drivers: string[];
}

export type OutcomeTone = 'good' | 'watch';

export interface IOutcomeMetric {
    id: OutcomeMetricId;
    title: string;
    /**
     * The headline figure, already formatted. Absent when there is nobody to count, and the caption
     * then says why: a zero there would read as a finding about clients nobody could assess.
     */
    value?: string;
    /** What the figure counts, with the base it was counted over. */
    caption: string;
    tone: OutcomeTone;
    /** Bea's read on it, in her voice, for under the figure. */
    narration: string;
    definition: IOutcomeDefinition;
    /** At most 19 characters, or the modal's title is cut off on a phone. */
    drillTitle: string;
    drillBlurb: string;
    /** What the chips under each client are, for a metric that lists any. */
    driversLabel?: string;
    /** Empty when there is nobody to list, which the modal says rather than showing a blank. */
    clients: IOutcomeClient[];
}

/**
 * The moment the digest was read for, which is what every rolling week here is anchored on.
 *
 * It comes off the digest rather than the clock so the figures and the findings behind them can
 * never be a day apart, and never off the last bucket, which starts up to six days before the read
 * and ends in the future.
 */
const readAtOf = (digest: IAgencyDigest): number => new Date(digest.readAt).getTime();

const observedAt = (finding: IAgencyFinding): number => new Date(finding.observedOn).getTime();

const monthDayOf = (at: number): string => {
    return new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const nameDomains = (domains: CareDomain[]): string => {
    const named = domains.map(domain => DOMAIN_LABEL[domain].toLowerCase());
    if (named.length <= 1) {
        return named.join('');
    }
    return `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`;
};

const plural = (count: number, one: string, many: string): string => (count === 1 ? one : many);

/** One week of a client's record, read the way the standing reads its recent weeks. */
interface IWeekRead {
    from: number;
    /** readCondition over the week: the most serious thing raised about each part of life, added up. */
    reading: number;
    /** Findings about a part of their life, as distinct from the arrangements around them. */
    aboutThem: number;
    hadIncident: boolean;
    /** Whether anything at all was written down that week, about them or not. */
    hadAnything: boolean;
    raised: CareDomain[];
}

/**
 * The weeks Back From a Decline reads, split where the map splits a client's usual from their recent
 * weeks (recentStartsOn), so no finding is part of the usual for one figure and judged by the other.
 *
 * The recent weeks roll back from the read, as many whole ones as always fit after the split. The
 * usual weeks are the chart's own buckets before it. Seven days on both sides, because a reading
 * counts the parts of life something was raised about and a longer span has longer to raise
 * something in: a week is judged against weeks, as the standing judges spans against spans.
 */
const weeksOf = (digest: IAgencyDigest): { recent: IWeekSpan[]; usual: IWeekSpan[] } => {
    const split = new Date(`${digest.recentStartsOn}T00:00:00.000Z`).getTime();
    const usual = digest.weekly
        .map(point => new Date(`${point.weekStartsOn}T00:00:00.000Z`).getTime())
        .filter(from => from + WEEK_MS <= split)
        .map(from => ({ from, to: from + WEEK_MS }));

    return { recent: rollingWeeksTo({ readAt: readAtOf(digest), count: RECENT_ROLLING_WEEKS }), usual };
};

const readWeek = (params: { findings: IAgencyFinding[]; span: IWeekSpan }): IWeekRead => {
    const { findings, span } = params;
    const inWeek = findings.filter(finding => observedAt(finding) >= span.from && observedAt(finding) < span.to);

    return {
        from: span.from,
        reading: readCondition(inWeek),
        aboutThem: inWeek.filter(finding => domainsOf(finding).length > 0).length,
        hadIncident: inWeek.some(finding => classifyFinding(finding) === 'incident'),
        hadAnything: inWeek.length > 0,
        raised: [...new Set(inWeek.flatMap(domainsOf))]
    };
};

/** A client's recent weeks, newest first, and the mean reading of their usual weeks. */
const weeklyCondition = (params: { digest: IAgencyDigest; careMapId: string }): { recent: IWeekRead[]; usual: number } => {
    const { digest, careMapId } = params;
    const theirs = digest.windowFindings.filter(finding => finding.careMapId === careMapId);
    const weeks = weeksOf(digest);

    const usualReadings = weeks.usual.map(span => readWeek({ findings: theirs, span }).reading);
    const usual = usualReadings.length === 0
        ? 0
        : usualReadings.reduce((total, reading) => total + reading, 0) / usualReadings.length;

    return { recent: weeks.recent.map(span => readWeek({ findings: theirs, span })), usual };
};

/** The standing's rule for a rise, applied to one week: what the map would have said about it alone. */
const wasAboveUsual = (week: IWeekRead, usual: number): boolean => {
    if (week.hadIncident) {
        return true;
    }
    return week.reading - usual > CONDITION_TOLERANCE && week.aboutThem >= MIN_RECENT_FINDINGS_TO_FLAG;
};

/**
 * Whether a week is back within the client's usual, which asks more than "not above it".
 *
 * Saying somebody is fine is the claim that needs evidence behind it, as everywhere on this tab. So
 * the week's reading has to sit within the tolerance of their usual however few findings are behind
 * it, and a week nobody wrote anything in is not a week anyone was back: it is a quiet record, which
 * must never read as a recovery.
 */
const isBackWithinUsual = (params: { week: IWeekRead; usual: number; client: IAgencyClient }): boolean => {
    const { week, usual, client } = params;
    const recorded = week.hadAnything
        || (client.lastRecordedAt !== undefined && new Date(client.lastRecordedAt).getTime() >= week.from);

    return recorded && !week.hadIncident && week.reading - usual <= CONDITION_TOLERANCE;
};

/** The week that most needs naming: an incident first, then the furthest above their usual. */
const worstOf = (weeks: IWeekRead[]): IWeekRead => {
    return weeks.reduce((worst, week) => {
        if (week.hadIncident !== worst.hadIncident) {
            return week.hadIncident ? week : worst;
        }
        return week.reading > worst.reading ? week : worst;
    });
};

/**
 * What was written down about a client while they were coming back.
 *
 * Deliberately the notes from the worst week onward, and only the ones that read as a response
 * rather than as the decline itself. That is the honest set: it is what somebody recorded around the
 * change, which is the most a record can say. Whether any of it caused the change is not knowable
 * from here and the copy never claims it.
 */
const recoveryNotesFor = (params: {
    digest: IAgencyDigest;
    careMapId: string;
    worst: IWeekRead;
    latest: IWeekRead;
}): string[] => {
    const { digest, careMapId, worst, latest } = params;

    const isRecoveryShaped = (finding: IAgencyFinding): boolean => {
        return finding.type === 'trend' || finding.type === 'recommendation';
    };

    const stillRaised = new Set(latest.raised);
    const quietNow = worst.raised.filter(domain => !stillRaised.has(domain));

    return digest.windowFindings
        .filter(finding => finding.careMapId === careMapId)
        .filter(finding => observedAt(finding) >= worst.from)
        .filter(isRecoveryShaped)
        .sort((a, b) => b.observedOn.localeCompare(a.observedOn))
        .slice(0, MAX_DRIVERS_PER_CLIENT)
        .map(finding => finding.title)
        .concat(quietNow.length > 0 ? [`Nothing raised about ${nameDomains(quietNow)} in the last 7 days`] : []);
};

/** The clients the two usual-based figures are counted over: the ones with enough earlier history to have a usual. */
const withUsualOf = (digest: IAgencyDigest): string[] => {
    return digest.baselines.filter(baseline => baseline.hasUsual).map(baseline => baseline.careMapId);
};

const usualBaseCaption = (base: number): string => {
    return base === 0
        ? 'No client has a usual to compare against yet'
        : `of ${base} ${plural(base, 'client', 'clients')} with a usual to compare against`;
};

/** Who came back, and how many had a decline they are not back from yet. */
const findBackFromADecline = (digest: IAgencyDigest, base: string[]): { back: IOutcomeClient[]; notBackYet: number } => {
    const onBook = new Map(digest.clients.map(client => [client.careMapId, client]));

    const back: IOutcomeClient[] = [];
    let notBackYet = 0;
    for (const careMapId of base) {
        const client = onBook.get(careMapId);
        const { recent, usual } = weeklyCondition({ digest, careMapId });
        const [latest, ...earlier] = recent;
        const above = earlier.filter(week => wasAboveUsual(week, usual));
        if (!client || !latest || above.length === 0) {
            continue;
        }
        if (!isBackWithinUsual({ week: latest, usual, client })) {
            notBackYet++;
            continue;
        }

        const worst = worstOf(above);
        const what = worst.hadIncident ? 'An incident recorded' : 'More raised than usual';
        back.push({
            careMapId,
            clientName: client.clientName,
            detail: `${what} in the week of ${monthDayOf(worst.from)}. Back within their usual in the last 7 days.`,
            drivers: recoveryNotesFor({ digest, careMapId, worst, latest })
        });
    }
    return { back, notBackYet };
};

const backFromADeclineNarration = (params: { base: number; back: number; notBackYet: number }): string => {
    const { base, back, notBackYet } = params;
    const before = `the ${RECENT_ROLLING_WEEKS - 1} weeks before the last 7 days`;

    if (base === 0) {
        return 'Nobody has enough history yet for me to say whether they came back from a decline.';
    }
    if (back > 0) {
        return `${back} ${plural(back, 'client was', 'clients were')} above their own usual in ${before} and ${plural(back, 'is', 'are')} back within it now.`;
    }
    return notBackYet === 0
        ? `Nobody I can compare had a decline in ${before} to come back from.`
        : `${notBackYet} ${plural(notBackYet, 'client', 'clients')} I can compare had a decline in ${before}, and none is back within their usual yet.`;
};

const buildBackFromADecline = (digest: IAgencyDigest): IOutcomeMetric => {
    const definition = OUTCOME_DEFINITIONS['back-from-a-decline'];
    const base = withUsualOf(digest);
    const { back, notBackYet } = findBackFromADecline(digest, base);

    return {
        id: 'back-from-a-decline',
        title: definition.title,
        value: base.length === 0 ? undefined : String(back.length),
        caption: usualBaseCaption(base.length),
        tone: 'good',
        narration: backFromADeclineNarration({ base: base.length, back: back.length, notBackYet }),
        definition,
        drillTitle: 'Back From a Decline',
        drillBlurb: 'Each client, the week of an incident or else the week they were furthest above their own usual, and what was recorded from then on. Those notes are what was on file at the time, not a proven cause.',
        driversLabel: 'What was recorded around the change',
        clients: back
    };
};

const buildSettledBelowUsual = (digest: IAgencyDigest): IOutcomeMetric => {
    const definition = OUTCOME_DEFINITIONS['settled-below-usual'];
    const base = withUsualOf(digest);
    const since = monthDayOf(new Date(`${digest.recentStartsOn}T00:00:00.000Z`).getTime());

    // The map's own Settled, so this figure and the green cells can never disagree about who.
    const settled: IOutcomeClient[] = digest.baselines
        .filter(baseline => baseline.standing === 'settled')
        .map(baseline => ({
            careMapId: baseline.careMapId,
            clientName: baseline.clientName,
            detail: `Less raised since ${since} than in their own earlier weeks, with no incident recorded.`,
            drivers: []
        }));

    const narration = base.length === 0
        ? 'No client has enough history yet for me to compare against their own usual.'
        : settled.length === 0
            ? `Nobody I can compare has settled below their own usual since ${since}.`
            : `${settled.length} ${plural(settled.length, 'client has', 'clients have')} settled below their own usual since ${since}, with no incident recorded.`;

    return {
        id: 'settled-below-usual',
        title: definition.title,
        value: base.length === 0 ? undefined : String(settled.length),
        caption: usualBaseCaption(base.length),
        tone: 'good',
        narration,
        definition,
        drillTitle: 'Settled Below Usual',
        drillBlurb: `Clients with more than a little less raised about them since ${since} than in their own earlier weeks, no incident recorded, and the household still writing. These are the clients the caseload map draws as Settled.`,
        clients: settled
    };
};

/** Who No Incident Recorded counts, who it leaves out, and the latest incident of each it counts against. */
const lastSevenDays = (digest: IAgencyDigest): {
    counted: IAgencyClient[];
    notCounted: IAgencyClient[];
    incidentFor: Map<string, IAgencyFinding>;
} => {
    const from = readAtOf(digest) - WEEK_MS;
    const inThem = digest.windowFindings.filter(finding => observedAt(finding) >= from);

    const incidentFor = new Map<string, IAgencyFinding>();
    for (const finding of [...inThem].sort((a, b) => a.observedOn.localeCompare(b.observedOn))) {
        if (classifyFinding(finding) === 'incident') {
            incidentFor.set(finding.careMapId, finding);
        }
    }

    const withFinding = new Set(inThem.map(finding => finding.careMapId));
    const recorded = (client: IAgencyClient): boolean => withFinding.has(client.careMapId)
        || (client.lastRecordedAt !== undefined && new Date(client.lastRecordedAt).getTime() >= from);

    return {
        counted: digest.clients.filter(recorded),
        notCounted: digest.clients.filter(client => !recorded(client)),
        incidentFor
    };
};

/**
 * The figure and its caption. Rounded down, so any incident keeps the share under 100%, and in whole
 * numbers before dividing, so 29 of 50 reads 58% rather than the 57 a float product floors to.
 */
const noIncidentFigure = (params: { clear: number; counted: number }): { value?: string; caption: string } => {
    const { clear, counted } = params;
    if (counted === 0) {
        return { caption: 'Nothing recorded for anyone in the last 7 days' };
    }
    if (counted < MIN_CLIENTS_FOR_A_RATE) {
        return { value: `${clear} of ${counted}`, caption: 'clients with something recorded in the last 7 days had no incident on the record' };
    }
    return {
        value: `${Math.floor((clear * 100) / counted)}%`,
        caption: `${clear} of ${counted} clients with something recorded in the last 7 days had no incident on the record`
    };
};

const noIncidentNarration = (params: { counted: number; hadIncident: number; notCounted: number; unreadable: number }): string => {
    const { counted, hadIncident, notCounted, unreadable } = params;
    const leftOut = notCounted === 0
        ? ''
        : ` ${notCounted} ${plural(notCounted, 'client had', 'clients had')} nothing recorded, so I left them out.`;

    if (counted === 0) {
        return `Nothing has been recorded for anyone in the last 7 days, so there is nothing for me to count.${describeUnreadable(unreadable)}`;
    }
    if (hadIncident === 0) {
        return `No incident on the record for ${plural(counted, 'the one client', `any of the ${counted} clients`)} with something recorded in the last 7 days.${leftOut}${describeUnreadable(unreadable, true)}`;
    }
    return `${hadIncident === 1 ? 'One client' : `${hadIncident} clients`} had an incident recorded in the last 7 days. Open this to see who, and what was written down.${leftOut}`;
};

/** What the list behind the figure holds, named so the title never claims an incident it does not show. */
const noIncidentList = (params: { hadIncident: number; notCounted: number }): Pick<IOutcomeMetric, 'drillTitle' | 'drillBlurb'> => {
    const { hadIncident, notCounted } = params;
    if (hadIncident > 0 && notCounted > 0) {
        return {
            drillTitle: 'Incidents and Quiet',
            drillBlurb: 'Clients with an incident on file in the last 7 days, then any client with nothing recorded in that time, who is not counted either way.'
        };
    }
    if (notCounted > 0) {
        return {
            drillTitle: 'Quiet Records',
            drillBlurb: 'Clients with nothing recorded in the last 7 days, who are not counted either way.'
        };
    }
    return {
        drillTitle: 'Incidents Recorded',
        drillBlurb: 'Clients with an incident on file in the last 7 days.'
    };
};

const buildNoIncidentRecorded = (digest: IAgencyDigest): IOutcomeMetric => {
    const definition = OUTCOME_DEFINITIONS['no-incident-recorded'];
    const { counted, notCounted, incidentFor } = lastSevenDays(digest);
    const hadIncident = counted.filter(client => incidentFor.has(client.careMapId));

    const clients: IOutcomeClient[] = [
        ...hadIncident.map(client => {
            const incident = incidentFor.get(client.careMapId);
            return {
                careMapId: client.careMapId,
                clientName: client.clientName,
                detail: incident ? `${incident.title}. ${incident.narrative}` : 'An incident was recorded in the last 7 days.',
                drivers: []
            };
        }),
        ...notCounted.map(client => ({
            careMapId: client.careMapId,
            clientName: client.clientName,
            detail: 'Nothing recorded in the last 7 days, so not counted either way.',
            drivers: []
        }))
    ];

    return {
        id: 'no-incident-recorded',
        title: definition.title,
        ...noIncidentFigure({ clear: counted.length - hadIncident.length, counted: counted.length }),
        // A clean week is only good news about the clients it covers. With somebody uncounted or
        // unread this is the most consequential sentence on the tab to get wrong, so it neither
        // reads as an all-clear nor claims the tone of one.
        tone: counted.length > 0 && hadIncident.length === 0 && notCounted.length === 0 && digest.unreadableCount === 0
            ? 'good'
            : 'watch',
        narration: noIncidentNarration({
            counted: counted.length,
            hadIncident: hadIncident.length,
            notCounted: notCounted.length,
            unreadable: digest.unreadableCount
        }),
        definition,
        ...noIncidentList({ hadIncident: hadIncident.length, notCounted: notCounted.length }),
        clients
    };
};

/**
 * One week's observations, grouped by the client they were written about.
 *
 * Grouped rather than listed flat because the reader's next question after "what happened that week"
 * is always "to whom", and a flat list of forty titles answers neither. Each client's own titles ride
 * along so the modal can show them without a second pass over the digest.
 */
export const observationsInWeek = (params: {
    digest: IAgencyDigest;
    weekStartsOn: string;
    kind?: AgencyAlertKind;
}): IOutcomeClient[] => {
    const { digest, weekStartsOn, kind } = params;
    const from = new Date(`${weekStartsOn}T00:00:00.000Z`).getTime();
    const to = from + WEEK_MS;

    const inWeek = digest.alerts
        .filter(alert => !kind || alert.kind === kind)
        .filter(alert => {
            const at = new Date(alert.observedOn).getTime();
            return at >= from && at < to;
        })
        .sort((a, b) => b.observedOn.localeCompare(a.observedOn));

    const byClient = new Map<string, { clientName: string; titles: string[] }>();
    for (const alert of inWeek) {
        const held = byClient.get(alert.careMapId) ?? { clientName: alert.clientName, titles: [] };
        held.titles.push(alert.title);
        byClient.set(alert.careMapId, held);
    }

    return [...byClient.entries()].map(([careMapId, held]) => ({
        careMapId,
        clientName: held.clientName,
        detail: `${held.titles.length} ${held.titles.length === 1 ? 'observation' : 'observations'}.`,
        drivers: held.titles
    }));
};

/**
 * The three outcome figures, in the order they answer an operator's questions: did anyone come back,
 * is anyone better off than their usual, and did anything happen this week.
 */
export const buildOutcomes = (digest: IAgencyDigest): IOutcomeMetric[] => {
    return [
        buildBackFromADecline(digest),
        buildSettledBelowUsual(digest),
        buildNoIncidentRecorded(digest)
    ];
};
